import { motion, useScroll, useTransform, useMotionTemplate, useSpring } from 'motion/react';
import { useEffect, useRef } from 'react';
import { useResponsiveValue, useLottie, Flex } from '@lemonade-hq/cdk';
import { mergeRefs } from 'react-merge-refs';

type LottieData = Record<string, unknown> | undefined;
interface ParallaxAnimationBoxProps {
  startPositionY: [number, number, number];
  endPositionY: [number, number, number];

  left: [number | string, number | string, number | string] | number | string;
  right: [number | string, number | string, number | string] | number | string;
  responsiveAnimation: [LottieData, LottieData] | [LottieData, LottieData, LottieData] | LottieData;

  play: boolean;
}
export const ParallaxAnimationBox: React.FC<ParallaxAnimationBoxProps> = ({
  startPositionY,
  endPositionY,
  responsiveAnimation,
  left,
  right,
  play,
}) => {
  const startValue = useResponsiveValue(startPositionY);
  const endValue = useResponsiveValue(endPositionY);
  const animationValue = useResponsiveValue(responsiveAnimation);
  const leftValue = useResponsiveValue(left);
  const rightValue = useResponsiveValue(right);
  const ref = useRef(null);
  const timeout = useRef<NodeJS.Timeout>();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: [`${startValue}px end`, `end ${endValue * -1}px`],
  });

  const spring = useSpring(scrollYProgress, {
    bounce: 0,
    duration: 1,
  });

  const y = useTransform(spring, [0, 1], [startValue, endValue]);
  const transform = useMotionTemplate`translateY(${y}px)`;

  const lottieRef = useRef<Element>(null);

  const stuffLottie = useLottie(lottieRef, {
    animationData: animationValue,
    lottieConfig: {
      loop: false,
      autoplay: true,
    },
  });

  useEffect(() => {
    if (play) {
      timeout.current = setTimeout(() => {
        stuffLottie?.play();
      }, 1000);
    } else {
      timeout.current = setTimeout(() => {
        stuffLottie?.goToAndStop(0);
      }, 1000);
    }

    return () => {
      clearTimeout(timeout.current);
    };
  }, [stuffLottie, play]);

  return (
    <motion.div
      className="div"
      ref={ref}
      style={{ transform, position: 'absolute', left: leftValue, right: rightValue }}
    >
      <Flex ref={mergeRefs([lottieRef])} />
    </motion.div>
  );
};
